// /frontend/src/api/apiUtils.js
/**
 * Single source of truth for:
 * - Backend origin & API base
 * - Asset URL building (logos)
 * - Axios instance + interceptors (401/public-safe)
 * - API helpers
 * - ✅ Global "default-logo.svg" autopatch (no component changes needed)
 */

import axios from "axios";

const IS_PROD = process.env.NODE_ENV === "production";
const RAW_ENV_BASE = (process.env.REACT_APP_API_BASE_URL || "").trim();

/* ---------- helpers ---------- */
function trimTrailingSlash(u) { return (u || "").replace(/\/+$/, ""); }
function stripApiSuffix(u) { return trimTrailingSlash(u).replace(/\/api$/i, ""); }
function ensureApiSuffix(u) { const s = trimTrailingSlash(u); return /\/api(?:\/)?$/i.test(s) ? s : `${s}/api`; }

/** runtime override: <meta name="x-api-base" content="https://..."> / window.__APP_CONFIG__.API_BASE */
function readRuntimeApiBase() {
  try {
    const meta = typeof document !== "undefined" ? document.querySelector('meta[name="x-api-base"]') : null;
    if (meta?.content) return meta.content.trim();
    if (typeof window !== "undefined" && window.__APP_CONFIG__?.API_BASE) {
      return String(window.__APP_CONFIG__.API_BASE).trim();
    }
  } catch {}
  return "";
}

/* ---------- origin / api base ---------- */
const PROD_SAFE_BACKEND = "https://cheetah-backend.up.railway.app";

export function getBackendOrigin() {
  const isCapacitor = typeof window !== "undefined" && !!window.Capacitor;
  const isAndroid = typeof navigator !== "undefined" && /Android/i.test(navigator.userAgent || "");
  const DEV_MOBILE_ORIGIN = "http://10.0.2.2:5000";

  if (IS_PROD) {
    const runtimeBase = readRuntimeApiBase();
    const origin = RAW_ENV_BASE || runtimeBase || PROD_SAFE_BACKEND;
    return stripApiSuffix(origin);
  }
  if (isCapacitor && isAndroid) return stripApiSuffix(DEV_MOBILE_ORIGIN);
  return stripApiSuffix(RAW_ENV_BASE || "http://127.0.0.1:5000");
}

export function getApiBase() {
  const isCapacitor = typeof window !== "undefined" && !!window.Capacitor;
  const isAndroid = typeof navigator !== "undefined" && /Android/i.test(navigator.userAgent || "");
  const DEV_MOBILE_ORIGIN = "http://10.0.2.2:5000";
  const origin = typeof window !== "undefined" ? window.location.origin : "";

  // เสิร์ฟ frontend จาก backend เดียวกัน
  if (origin.includes("127.0.0.1:5000") || origin.includes("localhost:5000")) {
    return "/api";
  }
  if (!IS_PROD && isCapacitor && isAndroid) return `${DEV_MOBILE_ORIGIN}/api`;
  if (process.env.REACT_APP_API_BASE_URL) return ensureApiSuffix(process.env.REACT_APP_API_BASE_URL);

  const base = ensureApiSuffix(getBackendOrigin());
  if (!IS_PROD) console.log("[API] base =", base);
  return base;
}

export function withBase(path) {
  const base = getApiBase().replace(/\/+$/, "");
  let p = String(path || "");
  if (/^https?:\/\//i.test(p)) return p;
  p = p.replace(/^\/+/, "").replace(/^api\/+/, "");
  return `${base}/${p}`;
}

export function withOrigin(path) {
  const origin = getBackendOrigin().replace(/\/+$/, "");
  let p = String(path || "");
  if (/^https?:\/\//i.test(p)) return p;
  p = p.replace(/^\/+/, "");
  return `${origin}/${p}`;
}

/* ---------- LOGO placeholder (DATA URI – no network) ---------- */
export const DEFAULT_LOGO_DATAURI =
  'data:image/svg+xml;utf8,' +
  encodeURIComponent(`
  <svg xmlns="http://www.w3.org/2000/svg" width="160" height="160" viewBox="0 0 160 160">
    <rect width="160" height="160" fill="#f3f4f6"/>
    <g fill="#9ca3af">
      <circle cx="80" cy="64" r="28"/>
      <rect x="28" y="98" width="104" height="34" rx="8"/>
    </g>
  </svg>`);

const DEFAULT_LOGO_RE = /(^|\/)default-logo\.svg(?:\?.*)?$/i;

/**
 * Clean logo URL:
 * - "" / *default-logo.svg → DATA URI
 * - data: / http(s): keep
 * - relative path → withOrigin
 */
export function sanitizeLogoUrl(url) {
  const raw = (url || "").trim();
  if (!raw || DEFAULT_LOGO_RE.test(raw)) return DEFAULT_LOGO_DATAURI;
  if (/^data:/i.test(raw)) return raw;
  if (/^https?:\/\//i.test(raw)) return raw;
  return withOrigin(raw.startsWith("/") ? raw : `/${raw}`);
}

/* ---------- Auth token helpers ---------- */
export function getAuthToken() {
  try { return localStorage.getItem("authToken") || ""; } catch { return ""; }
}
export function setAuthToken(token) {
  try {
    if (!token) localStorage.removeItem("authToken");
    else localStorage.setItem("authToken", token);
  } catch {}
}

/* ---------- Axios instance + interceptors ---------- */
const api = axios.create({
  baseURL: getApiBase(),
  // withCredentials: true, // เปิดถ้าใช้ cookie-based auth
});

// Endpoints ที่ "public และปลอดภัย" ถ้า 401 ห้ามเตะ logout/redirect
const PUBLIC_401_SAFE = [
  /\/api\/quotes\/public\//i,
  /\/api\/public\//i,
  /\/public\/desktop-survey/i,
  /\/api\/serp\//i,
  /\/api\/motor\/packages\/public\//i,
];

// Endpoints ที่ "สำคัญต่อสิทธิ์" ถ้า 401 ให้ logout/redirect
const AUTH_CRITICAL = [
  /\/api\/admins\/me$/i,
  /\/api\/admins\/.*$/i,
  /\/api\/users\/me$/i,
  /\/api\/auth\/refresh$/i,
];

// Request interceptor: แนบ Bearer เฉพาะ non-public และมี token
api.interceptors.request.use((config) => {
  const url = config.url || "";
  const isPublic = PUBLIC_401_SAFE.some((re) => re.test(url));

  // ไม่ส่ง Authorization ให้ public เพื่อกัน 401 แปลกๆ / CORS / cache
  if (isPublic) {
    if (config.headers && "Authorization" in config.headers) delete config.headers.Authorization;
    config.headers["Cache-Control"] = "no-cache";
  } else {
    const token = getAuthToken();
    if (token) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }
  }

  return config;
});

// Response interceptor: แยก 401 จาก public vs. auth-critical
api.interceptors.response.use(
  (res) => res,
  (error) => {
    const status = error?.response?.status;
    const url = error?.config?.url || "";

    if (status === 401) {
      const fromPublic = PUBLIC_401_SAFE.some((re) => re.test(url));
      const isAuthCritical = AUTH_CRITICAL.some((re) => re.test(url));

      // จาก public → ปล่อยให้ผู้เรียกจัดการเอง (ห้ามล้าง token/redirect)
      if (fromPublic) return Promise.reject(error);

      // สำคัญ → ล้าง token + ส่งไปหน้า login ที่ถูกต้อง
      if (isAuthCritical) {
        setAuthToken("");
        const isAdminArea = typeof window !== "undefined" && window.location.pathname.startsWith("/admin");
        if (typeof window !== "undefined") window.location.replace(isAdminArea ? "/admin/login" : "/login");
        return; // hard redirect
      }
    }

    return Promise.reject(error);
  }
);

/* ---------- API helpers ---------- */
export function handleApiError(error, context = "API") {
  if (error?.response) console.error(`🌐 [${context}] Status ${error.response.status}:`, error.response.data);
  else if (error?.request) console.error(`🚫 [${context}] No response from server:`, error.request);
  else console.error(`⚙️ [${context}] Request setup error:`, error.message);
  return null;
}

export function validateApiResponse(response, requiredKeys = []) {
  if (!response || typeof response !== "object") {
    console.error("[validateApiResponse] Invalid response structure:", response);
    return false;
  }
  for (const key of requiredKeys) {
    if (!(key in response)) {
      console.error(`[validateApiResponse] Missing key: ${key} in response`, response);
      return false;
    }
  }
  return true;
}

export const buildQueryParams = (params = {}) => new URLSearchParams(params).toString();
export const paginateResults = (results = [], page = 1, itemsPerPage = 10) =>
  results.slice((page - 1) * itemsPerPage, page * itemsPerPage);

export function extractResults(response, resultsKey = "results") {
  if (!response || !Array.isArray(response[resultsKey])) {
    console.warn("[extractResults] Invalid or missing results in response:", response);
    return [];
  }
  return response[resultsKey];
}

export async function handleApiRequest(requestFn, context = "API Request", fallback = null) {
  try {
    if (!IS_PROD) console.log(`🚀 [${context}] Sending request...`);
    const response = await requestFn();

    if (!response || typeof response !== "object") {
      if (!IS_PROD) console.warn(`⚠️ [${context}] Invalid response object.`);
      return fallback;
    }

    let contentType = "Unknown";
    const h = response.headers;
    if (h) contentType = typeof h.get === "function" ? (h.get("content-type") || "Unknown") : (h["content-type"] || "Unknown");

    if (!IS_PROD) console.log(`📡 [${context}] Response:`, {
      status: response.status ?? "Unknown",
      statusText: response.statusText ?? "Unknown",
      contentType,
    });

    if (response.status === 404) {
      if (!IS_PROD) console.warn(`⚠️ [${context}] 404 not found. Returning fallback.`);
      return fallback;
    }

    if (!("data" in response)) {
      if (!IS_PROD) console.warn(`⚠️ [${context}] Empty response structure.`);
      return fallback;
    }

    return response.data;
  } catch (error) {
    console.error(`❌ [${context}] API Error:`, error);
    if (error?.response) {
      console.warn(`⚠️ [${context}] Error Response:`, {
        status: error.response.status,
        statusText: error.response.statusText,
        data: error.response.data,
      });
    }
    return fallback;
  }
}

export async function fetchWithFallback(endpoint, options = {}, fallback = null) {
  try {
    if (!IS_PROD) console.log(`🚀 [API] Fetching ${endpoint} ...`);
    const res = await api.request({
      url: endpoint,
      method: options.method || "get",
      headers: options.headers || {},
      data: options.body || undefined,
      params: options.params || undefined,
    });
    const data = res.data;
    if (!data || typeof data !== "object") {
      if (!IS_PROD) console.warn(`⚠️ [API] Invalid response from ${endpoint}:`, data);
      return fallback;
    }
    if (!IS_PROD) console.log(`✅ [API] Fetched successfully from ${endpoint}:`, data);
    return data;
  } catch (error) {
    console.error(`❌ [API] Error fetching ${endpoint}:`, error?.message || error);
    return fallback;
  }
}

// helper ตรวจว่าเป็น public URL ตาม policy หรือไม่
function isPublicUrl(url = "") {
  const PUBLIC_401_SAFE = [
    /\/api\/quotes\/public\//i,
    /\/api\/public\//i,
    /\/public\/desktop-survey/i,
    /\/api\/serp\//i,
    /\/api\/motor\/packages\/public\//i,
  ];
  return PUBLIC_401_SAFE.some((re) => re.test(String(url)));
}

// ⚠️ REPLACE ฟังก์ชันเดิมทั้งหมดด้วยอันนี้
export async function retryRequest(requestFn, retries = 3, delay = 1000) {
  let attempt = 0;
  // ใช้ตัวแปรเก็บ URL ล่าสุดที่เรียก (ถ้า requestFn ส่ง axios config คืนใน error)
  let lastUrl = "";

  while (attempt < retries) {
    try {
      return await requestFn();
    } catch (error) {
      attempt += 1;
      // พยายามอ่าน URL จาก axios error (มีใน error.config.url)
      try { lastUrl = error?.config?.url || lastUrl; } catch {}

      const status = error?.response?.status;

      // ✅ ถ้าเจอ 401 จาก public endpoint — ห้าม redirect ออก ให้หยุด retry แล้วส่งคืน null
      if (status === 401 && isPublicUrl(lastUrl)) {
        console.warn(`🚫 [retryRequest] 401 from public endpoint (${lastUrl}). No redirect.`);
        return null;
      }

      // ❗ 401 จาก non-public → ค่อยจัดการ redirect ที่นี่ (สุดท้ายจริงๆ)
      if (status === 401) {
        console.warn("🚫 [retryRequest] Unauthorized after retry. Redirecting to /login.");
        if (typeof window !== "undefined") window.location.replace("/login");
        return null;
      }

      if (attempt >= retries) {
        console.error("❌ [retryRequest] All retries failed.");
        throw error;
      }

      if (process.env.NODE_ENV !== "production") {
        console.warn(`🔄 Retry ${attempt}/${retries}. Waiting ${delay}ms... (url=${lastUrl || "unknown"})`);
      }
      await new Promise((r) => setTimeout(r, delay));
      delay *= 2;
    }
  }
  return null;
}

/* =====================================================================================
   🔧 GLOBAL AUTOPATCH: เปลี่ยนทุกการอ้าง 'default-logo.svg' → DATA URI อัตโนมัติ
   - ครอบทั้ง element ที่มีอยู่แล้ว + element ที่ถูกเพิ่มใหม่
   - ครอบกรณี JS เซ็ต img.src ภายหลังด้วย (patch Image.prototype.src)
   ===================================================================================== */

(function installDefaultLogoAutopatch() {
  if (typeof window === "undefined" || typeof document === "undefined") return;

  const isDefault = (v) => DEFAULT_LOGO_RE.test(String(v || ""));

  // 1) sweep DOM ที่มีอยู่
  function sweepOnce(root = document) {
    try {
      const imgs = root.querySelectorAll ? root.querySelectorAll("img[src]") : [];
      imgs.forEach((img) => {
        const s = img.getAttribute("src");
        if (isDefault(s)) img.setAttribute("src", DEFAULT_LOGO_DATAURI);
      });
    } catch {}
  }

  // 2) เฝ้าดูการเปลี่ยนแปลง DOM
  try {
    const mo = new MutationObserver((mutations) => {
      for (const m of mutations) {
        if (m.type === "attributes" && m.attributeName === "src" && m.target?.tagName === "IMG") {
          const s = m.target.getAttribute("src");
          if (isDefault(s)) m.target.setAttribute("src", DEFAULT_LOGO_DATAURI);
        } else if (m.type === "childList" && (m.addedNodes?.length || 0) > 0) {
          m.addedNodes.forEach((n) => {
            if (n?.nodeType === 1) {
              if (n.tagName === "IMG") {
                const s = n.getAttribute("src");
                if (isDefault(s)) n.setAttribute("src", DEFAULT_LOGO_DATAURI);
              }
              sweepOnce(n);
            }
          });
        }
      }
    });
    mo.observe(document.documentElement || document, { childList: true, subtree: true, attributes: true, attributeFilter: ["src"] });
  } catch {}

  // 3) patch setter ของ Image.prototype.src ให้กรองก่อน
  try {
    const desc = Object.getOwnPropertyDescriptor(Image.prototype, "src");
    if (desc?.set && desc?.get) {
      Object.defineProperty(Image.prototype, "src", {
        configurable: true,
        enumerable: false,
        get() { return desc.get.call(this); },
        set(v) {
          const val = isDefault(v) ? DEFAULT_LOGO_DATAURI : v;
          return desc.set.call(this, val);
        },
      });
    }
  } catch {}

  // 4) run ครั้งแรกตอนพร้อม
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => sweepOnce(), { once: true });
  } else {
    sweepOnce();
  }
})();

/* ---------- exports ---------- */
export { api }; // เผื่อโมดูลอื่น import { api } ไปใช้ตรงๆ

export default {
  api,
  getBackendOrigin,
  getApiBase,
  withBase,
  withOrigin,
  DEFAULT_LOGO_DATAURI,
  sanitizeLogoUrl,
  handleApiError,
  validateApiResponse,
  buildQueryParams,
  paginateResults,
  extractResults,
  handleApiRequest,
  fetchWithFallback,
  retryRequest,
  getAuthToken,
  setAuthToken,
};
